import React, { useEffect, useState } from 'react';
import { Bell, CheckCircle, Loader2 } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { useAlertStore } from '@/stores/alertStore';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { apiClient } from '@/utils/apiClient';
import type { AlertRuleProposal, AlertRuleResponse } from '@/utils/apiClient';

// Resolve an alerts i18n key with a safe fallback to the key itself (as AlertSettings does).
const alabel = (t: any, key: string): string => (t?.alerts?.[key] ?? key);

const CONDITION_KEYS: Record<string, string> = {
  gt: 'condGt', lt: 'condLt', gte: 'condGte', lte: 'condLte', eq: 'condEq', neq: 'condNeq',
};
const CHANNEL_KEYS: Record<string, string> = {
  in_app: 'chInApp', telegram: 'chTelegram', email: 'chEmail', webhook: 'chWebhook',
};

/** A proposal the card can save: the right kind and every required field.
 *  An SLO rule names a service in ``metric_query`` ("" = every violated SLO)
 *  and has no condition or threshold. */
export function isUsableProposal(p: AlertRuleProposal | undefined | null): p is AlertRuleProposal {
  const r = p?.rule;
  if (!p || p.kind !== 'product_alert_rule' || !r) return false;
  if (typeof r.name !== 'string' || r.name.trim() === '') return false;
  if (typeof r.metric_query !== 'string') return false;
  if (!Array.isArray(r.notification_channels?.channels)) return false;
  if (r.rule_kind === 'slo') return true;
  return r.metric_query.trim() !== ''
    && typeof r.condition === 'string' && r.condition in CONDITION_KEYS
    && typeof r.threshold === 'number' && Number.isFinite(r.threshold);
}

/**
 * True when a stored rule has every field this proposal would save. The chat
 * answer (and its card) is persisted with the session, so after a reload the
 * card is back with its button; a rule that already matches is shown as
 * created instead of being created twice.
 */
export function isSameRule(saved: AlertRuleResponse, r: AlertRuleProposal['rule']): boolean {
  const chans = (c: unknown) => (Array.isArray(c) ? [...c].map(String).sort().join(',') : '');
  const kind = (k?: string | null) => (k === 'slo' ? 'slo' : 'metric');
  const nc = saved.notification_channels || {};
  return kind(saved.rule_kind) === kind(r.rule_kind)
    && saved.name === r.name
    && saved.metric_query === r.metric_query
    && saved.condition === r.condition
    && saved.threshold === r.threshold
    && saved.severity === r.severity
    && saved.evaluation_interval === r.evaluation_interval
    && (saved.entity_selector || '') === (r.entity_selector || '')
    && chans(nc.channels) === chans(r.notification_channels.channels)
    && (nc.webhook_url || '') === (r.notification_channels.webhook_url || '');
}

/** The existing rule this proposal duplicates, or null. Throws when the
 * rules cannot be listed — the caller decides what that means. */
async function findExisting(r: AlertRuleProposal['rule']): Promise<AlertRuleResponse | null> {
  const rules = await apiClient.listAlertRules();
  return (rules || []).find((saved) => isSameRule(saved, r)) || null;
}

/**
 * Every field that will be POSTED, one line each — the ConfirmDialog preview.
 * Built from the same `rule` object the confirmation sends, so the preview and
 * the write cannot disagree.
 */
export function proposalPreview(t: any, p: AlertRuleProposal): string[] {
  const r = p.rule;
  const d = new Set(p.defaults || []);
  const dflt = (on: boolean) => (on ? ` (${alabel(t, 'proposalDefault')})` : '');
  const severity = `${alabel(t, 'severity')}: ${alabel(t, r.severity)}${dflt(d.has('severity'))}`;
  const channels = `${alabel(t, 'channels')}: ${r.notification_channels.channels.map((c) => alabel(t, CHANNEL_KEYS[c] || c)).join(', ')}${dflt(d.has('channels'))}`;
  const lines = r.rule_kind === 'slo'
    ? [
        `${alabel(t, 'name')}: ${r.name}`,
        `${alabel(t, 'service')}: ${r.metric_query.trim() || alabel(t, 'proposalAllSlos')}`,
        severity,
        channels,
      ]
    : [
        `${alabel(t, 'name')}: ${r.name}`,
        `${alabel(t, 'metricQuery')}: ${r.metric_query}`,
        `${alabel(t, 'condition')}: ${alabel(t, CONDITION_KEYS[r.condition || ''] || r.condition || '')} ${r.threshold}${dflt(!!p.condition_defaulted)}`,
        `${alabel(t, 'entitySelector')}: ${r.entity_selector || alabel(t, 'proposalAllHosts')}`,
        severity,
        channels,
      ];
  if (r.notification_channels.webhook_url) {
    lines.push(`${alabel(t, 'webhookUrl')}: ${r.notification_channels.webhook_url}`);
  }
  lines.push(`${alabel(t, 'interval')}: ${alabel(t, 'proposalEvery').replace('{seconds}', String(r.evaluation_interval))}${dflt(d.has('interval'))}`);
  if (r.description) lines.push(`${alabel(t, 'description')}: ${r.description}`);
  return lines;
}

/**
 * STORY-275 — the chat proposed an application alert rule. Nothing is saved
 * until the user presses the button AND confirms in the STORY-155 dialog; the
 * write is the settings page's own create (alertStore → POST /api/alerts/rules)
 * with the user's session. Cancelling sends no request.
 */
export const AlertRuleProposalCard: React.FC<{ proposal: AlertRuleProposal }> = ({ proposal }) => {
  const { t } = useTranslation();
  const createRule = useAlertStore((s) => s.createRule);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [created, setCreated] = useState<string | null>(null);
  const [existed, setExisted] = useState(false);
  const [checking, setChecking] = useState(true);
  const [error, setError] = useState('');
  const usable = isUsableProposal(proposal);

  // A reloaded session brings this card back: if the rule is already stored,
  // say so and offer no button. A failed listing leaves the button live (the
  // create re-checks before it posts).
  useEffect(() => {
    if (!usable) return;
    let live = true;
    findExisting(proposal.rule)
      .then((hit) => {
        if (live && hit) {
          setCreated(hit.name);
          setExisted(true);
        }
      })
      .catch(() => undefined)
      .finally(() => live && setChecking(false));
    return () => {
      live = false;
    };
  }, [usable, proposal]);

  if (!usable) return null;
  const preview = proposalPreview(t, proposal);

  const doCreate = async () => {
    const r = proposal.rule;
    setSaving(true);
    setError('');
    try {
      // Re-check right before the write: another tab, or a reload racing the
      // first check, may have created it meanwhile. Only a listing FAILURE
      // lets the post go ahead unchecked — the endpoint itself cannot tell.
      const hit = await findExisting(r).catch(() => null);
      if (hit) {
        setCreated(hit.name);
        setExisted(true);
        setConfirming(false);
        return;
      }
      const shared = {
        name: r.name,
        metric_query: r.metric_query,
        severity: r.severity,
        notification_channels: { ...r.notification_channels },
        evaluation_interval: r.evaluation_interval,
        description: r.description || undefined,
      };
      const rule = await createRule(r.rule_kind === 'slo'
        ? { ...shared, rule_kind: 'slo' }
        : {
            ...shared,
            condition: r.condition ?? undefined,
            threshold: r.threshold ?? undefined,
            entity_selector: r.entity_selector || undefined,
          });
      setCreated(rule?.name || r.name);
      setConfirming(false);
    } catch (err: any) {
      setError(err?.message || alabel(t, 'saveFailed'));
      setConfirming(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-3 rounded-md border border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20 p-3 text-sm" data-testid="alert-rule-proposal">
      <div className="flex items-center gap-2 font-medium text-gray-900 dark:text-white">
        <Bell className="h-4 w-4" /> {alabel(t, 'proposalTitle')}
      </div>
      <ul className="mt-2 space-y-0.5 text-gray-700 dark:text-gray-200">
        {preview.map((line, i) => <li key={i} className="break-words">{line}</li>)}
      </ul>
      {error && <div className="mt-2 text-red-600 dark:text-red-400" role="alert">{error}</div>}
      {created ? (
        <div className="mt-2 flex items-center gap-1 text-green-700 dark:text-green-400" role="status">
          <CheckCircle className="h-4 w-4" /> {alabel(t, existed ? 'proposalAlreadyCreated' : 'proposalCreated').replace('{name}', created)}
        </div>
      ) : (
        <>
          <p className="mt-2 text-xs text-gray-600 dark:text-gray-400">{alabel(t, 'proposalHint')}</p>
          <button
            type="button"
            onClick={() => setConfirming(true)}
            disabled={saving || checking}
            className="mt-2 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {saving && <Loader2 className="h-4 w-4 animate-spin" />}
            {alabel(t, 'proposalCreate')}
          </button>
        </>
      )}
      <ConfirmDialog
        isOpen={confirming}
        title={alabel(t, 'proposalConfirmTitle')}
        message={alabel(t, 'proposalConfirmMessage')}
        preview={preview}
        confirmLabel={alabel(t, 'confirmSaveButton')}
        cancelLabel={alabel(t, 'back')}
        loading={saving}
        onConfirm={doCreate}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
};
